import React, { useState, useRef, useEffect } from 'react';
import { css, cx } from '@emotion/css';
import { IoShareOutline } from 'react-icons/io5';
import { FaWhatsapp, FaFacebook, FaInstagram } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

interface ShareButtonProps {
  url: string;
  title: string;
  className?: string;
  style?: React.CSSProperties;
}

const shareButton = css({
  width: '2rem',
  height: '2rem',
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backdropFilter: 'blur(5px)',
  ':hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    transform: 'scale(1.1)',
    boxShadow: 'var(--shadow-200)',
  },
  ':active': {
    transform: 'scale(0.95)',
  },
});

const buttonWrapper = css({
  position: 'relative',
});

const popup = css({
  position: 'absolute',
  top: 'calc(100% + 5px)',
  left: '0',
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-xs)',
  boxShadow: 'var(--shadow-400)',
  padding: 'var(--spacing-xs)',
  zIndex: 9999,
  minWidth: '200px',
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
});

const popupTitle = css({
  fontSize: 'var(--fs-350)',
  fontWeight: 600,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-2xs)',
});

const popupContent = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-3xs)',
});

const copyButton = css({
  padding: 'var(--spacing-3xs) var(--spacing-2xs)',
  backgroundColor: 'var(--pc-600)',
  color: 'var(--primary-color)',
  border: 'none',
  borderRadius: 'var(--spacing-xs)',
  fontSize: 'var(--fs-300)',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  ':hover': {
    backgroundColor: 'var(--pc-700)',
  },
  ':active': {
    transform: 'scale(0.98)',
  },
  ':disabled': {
    backgroundColor: '#10b981',
    cursor: 'default',
  },
});

const socialButtons = css({
  display: 'flex',
  gap: 'var(--spacing-3xs)',
  justifyContent: 'center',
  marginTop: 'var(--spacing-4xs)',
});

const socialButton = css({
  width: '2.5rem',
  height: '2.5rem',
  borderRadius: '50%',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  color: 'var(--primary-color)',
  ':hover': {
    transform: 'scale(1.1)',
  },
  ':active': {
    transform: 'scale(0.95)',
  },
  '&.facebook': {
    backgroundColor: '#1877F2',
  },
  '&.instagram': {
    background: 'linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)',
  },
  '&.whatsapp': {
    backgroundColor: '#25D366',
  },
  '&.twitter': {
    backgroundColor: '#000000',
  },
});

export const ShareButton: React.FC<ShareButtonProps> = ({ url, title, className, style }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [copied, setCopied] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const togglePopup = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPopup(!showPopup);
  };

  const copyUrlToClipboard = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const shareToSocialMedia = (platform: string) => {
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'instagram':
        copyUrlToClipboard();
        return;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodedTitle} - ${encodedUrl}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;
        break;
      default:
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank');
    }
    
    setShowPopup(false);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setShowPopup(false);
      }
    }

    if (showPopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPopup]);

  return (
    <>
      <div className={buttonWrapper}>
        <button 
          ref={buttonRef}
          className={cx(shareButton, className)}
          style={style}
          onClick={togglePopup}
          aria-label="Compartilhar"
        >
          <IoShareOutline size={18} />
        </button>
        
        {showPopup && (
          <div 
            ref={popupRef}
            className={popup}
          >
            <h3 className={popupTitle}>Compartilhar</h3>
            <div className={popupContent}>
              <button
                className={copyButton}
                onClick={copyUrlToClipboard}
                disabled={copied}
              >
                {copied ? 'Link copiado!' : 'Copiar link'}
              </button>
              
              <div className={socialButtons}>
                <button 
                  className={cx(socialButton, 'facebook')}
                  onClick={() => shareToSocialMedia('facebook')}
                  aria-label="Compartilhar no Facebook"
                >
                  <FaFacebook size={18} />
                </button>
                <button 
                  className={cx(socialButton, 'instagram')}
                  onClick={() => shareToSocialMedia('instagram')}
                  aria-label="Compartilhar no Instagram"
                >
                  <FaInstagram size={18} />
                </button>
                <button 
                  className={cx(socialButton, 'whatsapp')}
                  onClick={() => shareToSocialMedia('whatsapp')}
                  aria-label="Compartilhar no WhatsApp"
                >
                  <FaWhatsapp size={18} />
                </button>
                <button 
                  className={cx(socialButton, 'twitter')}
                  onClick={() => shareToSocialMedia('twitter')}
                  aria-label="Compartilhar no Twitter"
                >
                  <FaXTwitter size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ShareButton;
